import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import { getNameFromEmail } from "../../utils/common";

// Helper function to format full timestamp
function formatFullTimestamp(timestamp: any): string {
  if (!timestamp) return "No activity";

  let date: Date;

  // Handle Firestore timestamp object
  if (timestamp?.seconds) {
    date = new Date(timestamp.seconds * 1000);
  }
  // Handle Date object
  else if (timestamp instanceof Date) {
    date = timestamp;
  }
  // Handle numeric timestamp
  else if (typeof timestamp === "number") {
    date = new Date(timestamp * 1000);
  }
  // Handle string timestamp
  else if (typeof timestamp === "string") {
    date = new Date(timestamp);
  } else {
    return "Invalid timestamp";
  }

  if (isNaN(date.getTime())) return "Invalid date";

  return (
    date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }) +
    ", " +
    date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    })
  );
}

interface Team {
  id: string;
  teamName: string;
  default: boolean;
  orgId?: string;
  users?: {
    userId: string;
    role:
    | "customer_team_lead"
    | "customer_team_member"
    | "expert_team_lead"
    | "expert_team_member";
    joinedAt: any; // Timestamp
  }[];
  createdAt: any; // Timestamp
  updatedAt: any; // Timestamp
}

interface Member {
  id: string;
  uid: string;
  status: string;
  initials: string;
  name: string;
  role: string;
  email: string;
  sessions: string;
  lastActivity: string;
  lastActivitycustomer: string;
  lastActivityexpert: string;
  wallet: string;
  totalSpent: string;
  avatar: string | null;
  isOwner: boolean;
  invitedById?: string;
  invitedByName?: string;
  invitationId?: string;
  // Profile-specific fields
  rate?: string;
  earnings?: number;
  specialties?: string[];
  profileData?: any;
  bio?: string;
  experience?: string;
  languages?: string[];
  timeZone?: string;
  availability?: boolean;
  totalActivity?: string;
  // User collection fields
  userData?: {
    disabled?: boolean;
    [key: string]: any;
  };
}

interface TeamActivity {
  id: string;
  customerId: string;
  expertId: string;
  customerOrganizationId?: string;
  expertOrganizationId?: string;
  type: string;
  startTime: any;
  endTime?: any;
  duration: number | { hours: number; minutes: number; seconds: number };
  totalAmount: number;
  status: string;
  expertRating?: number;
  customerRating?: number;
  expertReview?: string;
  customerReview?: string;
  createdAt: any;
  updatedAt?: any;
  // Enhanced data from profiles
  customerData?: {
    name: string;
    email: string;
    profileImageUrl?: string;
    initials: string;
    teamRole?: string; // Role in the team
  };
  expertData?: {
    name: string;
    email: string;
    profileImageUrl?: string;
    initials: string;
    teamRole?: string; // Role in the team
  };
}

interface TeamState {
  teams: Team[];
  currentTeam: Team | null;
  members: Member[];
  filteredMembers: Member[];
  filteredMembersLoading?: boolean; // <-- add this line
  organization: any | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  teamRole: string;
  totalDistributed: number;
  // Add team activities
  teamActivities: TeamActivity[];
  activitiesLoading: boolean;
  activitiesError: string | null;
  // Real-time update tracking
  lastUpdated: string;
  realtimeListeners: any[];
  // Store last applied filters for reapplication
  lastAppliedFilters: {
    searchTerm?: string;
    userRole?: string;
    userId?: string;
  };
}

const initialState: TeamState = {
  teams: [],
  currentTeam: null,
  members: [],
  filteredMembers: [],
  filteredMembersLoading: false, // <-- add this line
  organization: null,
  status: "idle",
  teamRole: "",
  error: null,
  totalDistributed: 46.4,
  // Initialize team activities
  teamActivities: [],
  activitiesLoading: false,
  activitiesError: null,
  // Real-time updates
  lastUpdated: new Date().toISOString(),
  realtimeListeners: [],
  lastAppliedFilters: {},
  
};

// Real-time member updates with automatic filtering
export const setupRealtimeListeners = createAsyncThunk(
  "team/setupRealtimeListeners",
  async (
    {
      orgId,
      userId,
      userRole,
      searchTerm = "",
    }: {
      orgId: string;
      userId: string;
      userRole: string;
      searchTerm?: string;
    },
    { dispatch, getState }
  ) => {
    try {
      const listeners: any[] = [];

      // Listen to users collection for real-time member updates
      const usersQuery = query(
        collection(db, "users"),
        where("orgId", "==", orgId)
      );

      const usersUnsubscribe = onSnapshot(usersQuery, async (snapshot) => {
        console.log("Real-time users update detected");

        // Get current state
        const state = getState() as any;
        const currentMembers = state.team.members;

        // Process changes
        const changes = snapshot.docChanges();
        let needsRefetch = false;

        for (const change of changes) {
          if (change.type === "modified") {
            const userData = change.doc.data();
            const memberId = change.doc.id;

            // Check if this member exists in current members
            const memberExists = currentMembers.some(
              (m: any) => m.id === memberId || m.uid === memberId
            );

            if (memberExists) {
              needsRefetch = true;
              break;
            }
          }
        }

        if (needsRefetch) {
          // Dispatch a quick member update
          await dispatch(
            updateMembersFromRealtimeData({
              orgId,
              userId,
              userRole,
              searchTerm,
            })
          );
        }
      });

      listeners.push(usersUnsubscribe);

      // Listen to profiles collection for real-time profile updates
      const profilesQuery = collection(db, "profiles");
      const profilesUnsubscribe = onSnapshot(
        profilesQuery,
        async (snapshot) => {
          console.log("Real-time profiles update detected");

          const changes = snapshot.docChanges();
          let needsUpdate = false;

          for (const change of changes) {
            if (change.type === "modified") {
              const profileData = change.doc.data();
              const state = getState() as any;
              const currentMembers = state.team.members;

              // Check if this profile belongs to a current member
              const memberExists = currentMembers.some(
                (m: any) =>
                  m.uid === profileData.userId || m.id === profileData.userId
              );

              if (memberExists) {
                needsUpdate = true;
                break;
              }
            }
          }

          if (needsUpdate) {
            await dispatch(
              updateMembersFromRealtimeData({
                orgId,
                userId,
                userRole,
                searchTerm,
              })
            );
          }
        }
      );

      listeners.push(profilesUnsubscribe);

      // --- Add real-time invitations listener ---
      const invitationsQuery = query(
        collection(db, "invitations"),
        where("orgId", "==", orgId)
      );
      const invitationsUnsubscribe = onSnapshot(
        invitationsQuery,
        async (snapshot) => {
          console.log("Real-time invitations update detected");
          // Always refresh all team data to get latest invitations
          const state = getState() as any;
          const user = state?.auth?.user || {};
          // Use the latest user/team/org info from state if available
          await dispatch(
            fetchAllTeamData({
              orgId,
              userId,
              currentId: user?.uid || userId,
              userRole: user?.teamRole || userRole,
            })
          );
        }
      );
      listeners.push(invitationsUnsubscribe);
      // --- End invitations listener ---

      // Store listeners for cleanup
      dispatch(setRealtimeListeners(listeners));

      return { success: true, listenersCount: listeners.length };
    } catch (error: any) {
      console.error("Error setting up real-time listeners:", error);
      return { success: false, error: error.message };
    }
  }
);

// Quick member update from real-time data
export const updateMembersFromRealtimeData = createAsyncThunk(
  "team/updateMembersFromRealtimeData",
  async (
    {
      orgId,
      userId,
      userRole,
      searchTerm = "",
    }: {
      orgId: string;
      userId: string;
      userRole: string;
      searchTerm?: string;
    },
    { dispatch, getState }
  ) => {
    try {
      const state = getState() as any;
      const currentMembers = state.team.members;

      if (!currentMembers || currentMembers.length === 0) {
        return { members: [], filtered: [] };
      }

      // Get user IDs from current members
      const userIds = currentMembers.map((m: any) => m.uid || m.id);

      // Fetch updated user data
      const updatedUserData: Record<string, any> = {};
      const userDocs = await Promise.all(
        userIds.map((id: string) => getDoc(doc(db, "users", id)))
      );

      userDocs.forEach((userDoc) => {
        if (userDoc.exists()) {
          updatedUserData[userDoc.id] = userDoc.data();
        }
      });

      // Fetch updated profile data
      const updatedProfileData: Record<string, any> = {};
      const profilesRef = collection(db, "profiles");

      // Batch fetch profiles for current members
      for (let i = 0; i < userIds.length; i += 10) {
        const batch = userIds.slice(i, i + 10);
        const profileQuery = query(profilesRef, where("userId", "in", batch));
        const profileSnapshot = await getDocs(profileQuery);

        profileSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.userId) {
            updatedProfileData[data.userId] = data;
          }
        });
      }

      // Update members with new data
      const updatedMembers = currentMembers.map((member: any) => {
        const memberId = member.uid || member.id;
        const newUserData = updatedUserData[memberId];
        const newProfileData = updatedProfileData[memberId];

        if (newUserData || newProfileData) {
          return {
            ...member,
            // Update user data fields
            ...(newUserData && {
              name: newUserData.name || member.name,
              email: newUserData.email || member.email,
              status: newUserData.status || member.status,
              userData: {
                ...member.userData,
                ...newUserData,
                disabled:
                  newUserData.disabled !== undefined
                    ? newUserData.disabled
                    : member.userData?.disabled,
              },
            }),
            // Update profile data fields
            ...(newProfileData && {
              name: newProfileData.name || member.name,
              email: newProfileData.email || member.email,
              rate: newProfileData.ratePerMinute
                ? `$${Number(newProfileData.ratePerMinute).toFixed(2)}`
                : member.rate,
              specialties:
                newProfileData.specialities ||
                newProfileData.expertise ||
                member.specialties ||
                [],
              profileData: {
                ...member.profileData,
                ...newProfileData,
              },
            }),
          };
        }

        return member;
      });

      // Apply filtering
      dispatch(
        filterMembersTeams({
          searchTerm,
          userRole,
          userId,
        })
      );

      return { members: updatedMembers, filtered: updatedMembers };
    } catch (error: any) {
      console.error("Error updating members from real-time data:", error);
      throw error;
    }
  }
);

// Cleanup real-time listeners
export const cleanupRealtimeListeners = createAsyncThunk(
  "team/cleanupRealtimeListeners",
  async (_, { getState, dispatch }) => {
    try {
      const state = getState() as any;
      const listeners = state.team.realtimeListeners;

      if (listeners && listeners.length > 0) {
        listeners.forEach((unsubscribe: () => void) => {
          if (typeof unsubscribe === "function") {
            unsubscribe();
          }
        });

        dispatch(setRealtimeListeners([]));
        console.log(`Cleaned up ${listeners.length} real-time listeners`);
      }

      return { success: true };
    } catch (error: any) {
      console.error("Error cleaning up real-time listeners:", error);
      return { success: false, error: error.message };
    }
  }
);

// Update specific member data after profile/user update
export const updateMemberAfterSave = createAsyncThunk(
  "team/updateMemberAfterSave",
  async (
    {
      memberId,
      updatedData,
    }: {
      memberId: string;
      updatedData: any;
    },
    { dispatch, getState }
  ) => {
    try {
      const state = getState() as any;
      const currentMembers = state.team.members;

      // Find and update the specific member
      const updatedMembers = currentMembers.map((member: any) => {
        if (member.id === memberId || member.uid === memberId) {
          return {
            ...member,
            // Update basic fields
            name: updatedData.name || member.name,
            rate: updatedData.ratePerMin
              ? `$${Number(updatedData.ratePerMin).toFixed(2)}`
              : member.rate,

            // Update profile data
            profileData: {
              ...member.profileData,
              name: updatedData.name || member.profileData?.name,
              ratePerMinute:
                updatedData.ratePerMin || member.profileData?.ratePerMinute,
              teamRole: updatedData.role || member.profileData?.teamRole,
              specialities:
                updatedData.specialities || member.profileData?.specialities,
              availability:
                updatedData.availability !== undefined
                  ? updatedData.availability
                  : member.profileData?.availability,
            },

            // Update user data
            userData: {
              ...member.userData,
              name: updatedData.name || member.userData?.name,
              teamRole: updatedData.role || member.userData?.teamRole,
              uiRole: updatedData.role || member.userData?.uiRole,
              specialities:
                updatedData.specialities || member.userData?.specialities,
              availability:
                updatedData.availability !== undefined
                  ? updatedData.availability
                  : member.userData?.availability,
            },
          };
        }
        return member;
      });

      // Update the members in state
      dispatch(setEnhancedMembers(updatedMembers));

      // Apply current filtering to show updated data
      const currentFilters = state.team.lastAppliedFilters || {};
      dispatch(
        filterMembersTeams({
          searchTerm: currentFilters.searchTerm || "",
          userRole: currentFilters.userRole || "",
          userId: currentFilters.userId || "",
        })
      );

      return { success: true, memberId, updatedData };
    } catch (error: any) {
      console.error("Error updating member after save:", error);
      return { success: false, error: error.message };
    }
  }
);

// Fetch organization details
export const fetchOrganization = createAsyncThunk(
  "team/fetchOrganization",
  async (orgId: string, { rejectWithValue }) => {
    try {
      const orgDoc = await getDoc(doc(db, "organizations", orgId));
      if (orgDoc.exists()) {
        return { id: orgDoc.id, ...orgDoc.data() };
      }
      return rejectWithValue("Organization not found");
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch organization");
    }
  }
);

// Fetch teams for an organization
export const fetchTeams = createAsyncThunk(
  "team/fetchTeams",
  async (
    { orgId, teamId }: { orgId: string; teamId: string },
    { rejectWithValue }
  ) => {
    try {
      // If teamId is provided, fetch only that specific team
      if (teamId) {
        const teamDocRef = doc(db, `organizations/${orgId}/teams`, teamId);
        const teamDoc = await getDoc(teamDocRef);
        console.log("Fetching specific team:", teamId, teamDoc.exists(), orgId);
        if (teamDoc.exists()) {
          const data = teamDoc.data();
          console.log("Fdataetched specific team :", data);
          return [
            {
              id: teamDoc.id,
              teamName: data.teamName || "Unnamed Team",
              default: data.default || false,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
              users: data.users || [],
              orgId: orgId,
            },
          ];
        } else {
          return [];
        }
      }

      // Otherwise fetch all teams
      const snapshot = await getDocs(
        collection(db, `organizations/${orgId}/teams`)
      );
      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          teamName: data.teamName || "Unnamed Team",
          default: data.default || false,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          users: data.users || [],
          orgId: orgId,
        };
      });
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch teams");
    }
  }
);

// Fetch users for teams
export const fetchUsers = createAsyncThunk(
  "team/fetchUsers",
  async (
    { userIds, orgId }: { userIds: string[]; orgId: string },
    { rejectWithValue }
  ) => {
    try {
      if (userIds.length === 0) {
        return await getDocs(
          query(collection(db, "users"), where("orgId", "==", orgId))
        );
      }

      try {
        const userDocs = await Promise.all(
          userIds.map((id) => getDoc(doc(db, "users", id)))
        );
        const filtered = userDocs.filter((doc) => doc.exists());

        if (filtered.length > 0) {
          return {
            docs: filtered.map((doc) => ({
              id: doc.id,
              data: () => doc.data(),
            })),
          };
        }
      } catch (err) {
        console.warn(
          "Failed to fetch specific users, falling back to org:",
          err
        );
      }

      return await getDocs(
        query(collection(db, "users"), where("orgId", "==", orgId))
      );
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch users");
    }
  }
);

// Fetch users from specific team path
export const fetchTeamUsers = createAsyncThunk(
  "team/fetchTeamUsers",
  async (
    { orgId, userId }: { orgId: string; userId: string },
    { rejectWithValue }
  ) => {
    try {
      // First, get the team document from the specific path
      const teamDocRef = doc(db, `organizations/${orgId}/teams`, userId);
      const teamDoc = await getDoc(teamDocRef);

      if (!teamDoc.exists()) {
        console.log(
          `No team document found for user: ${userId} in org: ${orgId}`
        );
        return { docs: [], teamData: null };
      }

      const teamData = teamDoc.data();
      console.log("Team data found:", teamData);

      const userIds = teamData.users?.map((user: any) => user.userId) || [];

      if (userIds.length === 0) {
        console.log("No users in team, returning team data only");
        return { docs: [], teamData: teamData };
      }

      // Fetch user documents for the team members from users collection
      const userDocs = await Promise.all(
        userIds.map((id: string) => getDoc(doc(db, "users", id)))
      );

      const validUserDocs = userDocs.filter((doc) => doc.exists());
      console.log(`Found ${validUserDocs.length} valid user documents`);

      return {
        docs: validUserDocs.map((doc) => ({
          id: doc.id,
          data: () => doc.data(),
        })),
        teamData: teamData,
      };
    } catch (error: any) {
      console.error("Error in fetchTeamUsers:", error);
      return rejectWithValue(error?.message || "Failed to fetch team users");
    }
  }
);

// Fetch profiles data for members
export const fetchMembersProfiles = createAsyncThunk(
  "team/fetchMembersProfiles",
  async (userIds: string[], { rejectWithValue }) => {
    try {
      if (userIds.length === 0) return {};

      const profilesData: Record<string, any> = {};

      // Batch fetch profiles collection data
      const profilesRef = collection(db, "profiles");
      const profileBatches = [];

      // Firestore 'in' queries are limited to 10 items
      for (let i = 0; i < userIds.length; i += 10) {
        const batch = userIds.slice(i, i + 10);
        const batchQuery = query(profilesRef, where("userId", "in", batch));
        profileBatches.push(getDocs(batchQuery));
      }

      const profileResults = await Promise.all(profileBatches);

      // Process profile results
      profileResults.forEach((snapshot) => {
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.userId) {
            profilesData[data.userId] = {
              ...data,
              profileId: doc.id,
            };
          }
        });
      });

      return profilesData;
    } catch (error: any) {
      console.error("Error fetching members profiles:", error);
      return rejectWithValue(error?.message || "Failed to fetch profiles");
    }
  }
);

// Build member data from user documents and teams
export const buildMemberData = createAsyncThunk(
  "team/buildMemberData",
  async (
    {
      snapshot,
      teams,
      ownerUserId,
    }: { snapshot: any; teams: Team[]; ownerUserId: string | null },
    { rejectWithValue }
  ) => {
    try {
      const members = await Promise.all(
        snapshot.docs.map(async (userDoc: any) => {
          const userData = userDoc.data();
          console.log("Building member data for user:", userDoc.id, userData);
          const isOwner = ownerUserId === userDoc.id;
          const initials = userData.name
            ? userData.name
              .split(" ")
              .map((n: string) => n[0])
              .join("")
              .toUpperCase()
            : "";

          let userRole = isOwner
            ? "Team Owner"
            : userData.uiRole === "customer_team_lead" ||
              userData.uiRole === "expert_team_lead"
              ? "Team Lead"
              : "Individual";

          if (!isOwner) {
            teams.forEach((team) => {
              const member = team.users?.find((u) => u.userId === userDoc.id);
              if (member) {
                userRole = member.role;
              }
            });
          }

          // Fetch profile data from profiles collection
          let profileData = null;
          let ratePerMinute = "$0.00";

          let specialties = [];

          try {
            const profilesRef = collection(db, "profiles");
            const profileQuery = query(
              profilesRef,
              where("userId", "==", userDoc.id)
            );
            const profileSnapshot = await getDocs(profileQuery);

            if (!profileSnapshot.empty) {
              profileData = profileSnapshot.docs[0].data();

              // Extract rate per minute
              if (profileData.ratePerMinute || profileData.rate) {
                const rate = profileData.ratePerMinute || profileData.rate;
                ratePerMinute = `$${Number(rate).toFixed(2)}`;
              }

              // Extract specialties
              if (
                profileData.specialties &&
                Array.isArray(profileData.specialties)
              ) {
                specialties = profileData.specialties;
              } else if (
                profileData.expertise &&
                Array.isArray(profileData.expertise)
              ) {
                specialties = profileData.expertise;
              }
            }
          } catch (err) {
            console.error(
              `Error fetching profile for user ${userDoc.id}:`,
              err
            );
          }

          // Wallet balance fetching with validation
          let walletBalance = "$0.00";
          try {
            const walletDocRef = doc(db, "wallets", userDoc.id);
            const walletDoc = await getDoc(walletDocRef);

            if (walletDoc.exists()) {
              const walletData = walletDoc.data();
              const rawBalance = walletData?.balance;

              const balance =
                typeof rawBalance === "number"
                  ? rawBalance
                  : typeof rawBalance === "string"
                    ? parseFloat(rawBalance)
                    : "-";

              walletBalance = isNaN(balance)
                ? "$0.00"
                : `$${balance.toFixed(2)}`;

              // Calculate total earnings from wallet transactions

            } else {
              console.warn(`No wallet document found for user: ${userDoc.id}`);
            }
          } catch (err) {
            console.error(
              `Error fetching wallet balance for user ${userDoc.id}:`,
              err
            );
          }

          // Fetch total sessions from sessionActivity collection
          let totalSessions = "0";
          let totalSpent = "$0.00";
          let totalEarnings = "$0.00";


          try {
            const sessionActivityRef = collection(db, "sessionActivity");

            // Query for sessions where the user is either customer or expert
            const customerQuery = query(
              sessionActivityRef,
              where("customerId", "==", userDoc.id)
              // lead login to customerId match
              // where("customerId", "==", userDoc.id)
            );

            // Execute both queries
            const [customerSnapshot] = await Promise.all([
              getDocs(customerQuery),
            ]);

            // Combine the results and remove duplicates based on session ID
            const allSessions = new Set();
            let spentSum = 0;

            customerSnapshot.docs.forEach((doc) => {
              allSessions.add(doc.id);

              // Calculate total spent from cost field
              const sessionData = doc.data();
              if (sessionData.cost || sessionData.totalAmount) {
                const amount = sessionData.cost || sessionData.totalAmount;
                if (typeof amount === "number") {
                  spentSum += amount;
                } else if (typeof amount === "string") {
                  const parsedAmount = parseFloat(amount);
                  if (!isNaN(parsedAmount)) {
                    spentSum += parsedAmount;
                  }
                }
              }
            });

            totalSessions = allSessions.size.toString();
            if (spentSum > 0) {
              totalSpent = `$${spentSum.toFixed(2)}`;
            }
          } catch (err) {
            console.error(
              `Error fetching session count for user ${userDoc.id}:`,
              err
            );
          }

          // Calculate total earnings from sessionActivity collection where user is expert
          try {
            const sessionActivityRef = collection(db, "sessionActivity");
            const expertQuery = query(
              sessionActivityRef,
              where("expertId", "==", userDoc.id)
            );

            const expertSnapshot = await getDocs(expertQuery);
            let earningsSum = 0;

            expertSnapshot.docs.forEach((doc) => {
              const sessionData = doc.data();
              if (sessionData.cost) {
                const amount = sessionData.cost;
                if (typeof amount === "number") {
                  earningsSum += amount;
                } else if (typeof amount === "string") {
                  const parsedAmount = parseFloat(amount);
                  if (!isNaN(parsedAmount)) {
                    earningsSum += parsedAmount;
                  }
                }
              }
            });

            if (earningsSum > 0) {
              totalEarnings = `$${earningsSum.toFixed(2)}`;
            }
          } catch (err) {
            console.error(
              `Error fetching expert earnings for user ${userDoc.id}:`,
              err
            );
          }

          let totalActivity = "0";
          try {
            const sessionActivityRef = collection(db, "sessionActivity");

            // Query for sessions where the user is either customer or expert
            const expertQuery = query(
              sessionActivityRef,

              // // lead login to expertId match
              where("expertId", "==", userDoc.id)
            );

            // Execute both queries
            const [expertSnapshot] = await Promise.all([getDocs(expertQuery)]);

            // Combine the results and remove duplicates based on session ID
            const allSessionsActivity = new Set();
            expertSnapshot.docs.forEach((doc) =>
              allSessionsActivity.add(doc.id)
            );

            totalActivity = allSessionsActivity.size.toString();
          } catch (err) {
            console.error(
              `Error fetching session count for user ${userDoc.id}:`,
              err
            );
          }
          let lastActivitycustomer = "Never";
          try {
            const sessionActivityRef = collection(db, "sessionActivity");

            // Query for sessions where the user is customer
            const customerQuery = query(
              sessionActivityRef,
              where("customerId", "==", userDoc.id)
            );

            // Execute query
            const customerSnapshot = await getDocs(customerQuery);

            if (!customerSnapshot.empty) {
              // Find the latest activity based on startTime
              let latestTimestamp: any = null;

              customerSnapshot.docs.forEach((doc) => {
                const data = doc.data();
                const startTime = data.startTime;

                if (startTime) {
                  if (!latestTimestamp) {
                    latestTimestamp = startTime;
                  } else {
                    // Compare timestamps (handle both Firestore timestamps and regular dates)
                    const currentTime = startTime.seconds
                      ? startTime.seconds
                      : startTime instanceof Date
                        ? startTime.getTime() / 1000
                        : new Date(startTime).getTime() / 1000;
                    const latestTime = latestTimestamp.seconds
                      ? latestTimestamp.seconds
                      : latestTimestamp instanceof Date
                        ? latestTimestamp.getTime() / 1000
                        : new Date(latestTimestamp).getTime() / 1000;

                    if (currentTime > latestTime) {
                      latestTimestamp = startTime;
                    }
                  }
                }
              });

              // Format the latest activity time using the formatFullTimestamp function
              lastActivitycustomer = formatFullTimestamp(latestTimestamp);
            } else {
              lastActivitycustomer = "No activity";
            }
          } catch (err) {
            console.error(
              `Error fetching last activity time for user ${userDoc.id}:`,
              err
            );
            lastActivitycustomer = "Error fetching";
          }
          let lastActivityexpert = "Never";
          try {
            const sessionActivityRef = collection(db, "sessionActivity");

            // Query for sessions where the user is customer
            const customerQuery = query(
              sessionActivityRef,
              where("expertId", "==", userDoc.id)
            );

            // Execute query
            const customerSnapshot = await getDocs(customerQuery);

            if (!customerSnapshot.empty) {
              // Find the latest activity based on startTime
              let latestTimestamp: any = null;

              customerSnapshot.docs.forEach((doc) => {
                const data = doc.data();
                const startTime = data.startTime;

                if (startTime) {
                  if (!latestTimestamp) {
                    latestTimestamp = startTime;
                  } else {
                    // Compare timestamps (handle both Firestore timestamps and regular dates)
                    const currentTime = startTime.seconds
                      ? startTime.seconds
                      : startTime instanceof Date
                        ? startTime.getTime() / 1000
                        : new Date(startTime).getTime() / 1000;
                    const latestTime = latestTimestamp.seconds
                      ? latestTimestamp.seconds
                      : latestTimestamp instanceof Date
                        ? latestTimestamp.getTime() / 1000
                        : new Date(latestTimestamp).getTime() / 1000;

                    if (currentTime > latestTime) {
                      latestTimestamp = startTime;
                    }
                  }
                }
              });

              // Format the latest activity time using the formatFullTimestamp function
              lastActivityexpert = formatFullTimestamp(latestTimestamp);
            } else {
              lastActivityexpert = "No activity";
            }
          } catch (err) {
            console.error(
              `Error fetching last activity time for user ${userDoc.id}:`,
              err
            );
            lastActivityexpert = "Error fetching";
          }

          return {
            id: userDoc.id,
            uid: userDoc.id,
            status: userData.status || "active",
            initials,
            name: profileData?.name || "",
            role: userRole,
            email: userData.email || profileData?.email || "",
            sessions: totalSessions,
            totalActivity: totalActivity,
            lastActivitycustomer: lastActivitycustomer,
            lastActivityexpert: lastActivityexpert,
            lastActivity: userData.lastActive
              ? new Date(
                userData.lastActive.seconds * 1000
              ).toLocaleDateString()
              : "-",
            wallet: walletBalance,
            totalSpent: totalSpent,
            avatar: profileData?.profileImageUrl || null,
            isOwner,
            invitedById: userData.invitedById,
            invitedByName: userData.invitedByName,
            // Enhanced profile data
            rate: profileData?.ratePerMinute,
            earnings: totalEarnings,
            specialties: specialties,
            profileData: profileData,
            // User collection data including disabled status
            userData: {
              disabled: userData.disabled || false,
              ...userData,
            },
          };
        })
      );
      return members;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to build member data");
    }
  }
);

// Fetch invitations
export const fetchInvitations = createAsyncThunk(
  "team/fetchInvitations",
  async (
    { orgId, teamId }: { orgId: string; teamId?: string },
    { rejectWithValue }
  ) => {
    try {
      let invitationsQuery;
      console.log("Fetching invitations for orgId:", orgId, "teamId:", teamId);
      // Build query based on parameters
      if (orgId && teamId) {
        // Fetch invitations for specific organization and team
        invitationsQuery = query(
          collection(db, "invitations"),
          where("orgId", "==", orgId),
          where("invitedById", "==", teamId)
        );
      } else if (orgId) {
        // Fetch all invitations for the organization
        invitationsQuery = query(
          collection(db, "invitations"),
          where("orgId", "==", orgId)
        );
      } else {
        // Default fallback
        invitationsQuery = collection(db, "invitations");
      }

      const snapshot = await getDocs(invitationsQuery);
      const invitationsPromises = snapshot.docs.map(async (doc) => {
        const data = doc.data();
        const name =
          data.firstName || data.invitedName || getNameFromEmail(data.email);
        const initials = name
          .split(" ")
          .map((n: string) => n[0])
          .join("")
          .toUpperCase();

        const invitation = {
          id: doc.id,
          uid: data.uid || doc.id, // Use uid from invitation data if available
          status:
            data.status === "pending" ? "invited" : data.status || "invited",
          name,
          initials,
          role: data.teamRole,
          rate: "-",
          email: data.email,
          sessions: "0",
          wallet: "$0.00",
          totalSpent: "$0.00",
          avatar: null,
          isOwner: false,
          lastActivity: data.createdAt
            ? new Date(data.createdAt.seconds * 1000).toLocaleDateString()
            : "-",
          invitedById: data.invitedById,
          invitedByName: data.invitedByName,
          invitationId: doc.id,
          createdAt: data.createdAt,
        };

        // If status is "accepted", find uid in users collection by matching email
        if (data.status === "accepted") {
          try {
            const userQuery = query(
              collection(db, "users"),
              where("email", "==", data.email)
            );
            const userSnapshot = await getDocs(userQuery);
            if (!userSnapshot.empty) {
              const userDoc = userSnapshot.docs[0];
              const userData = userDoc.data() as any;
              // Update invitation with actual user data
              return {
                ...invitation,
                id: userDoc.id,
                uid: userDoc.id,
                status: "active",
                name: userData.name || invitation.name,
                avatar: userData.photoURL || null,
                sessions: userData.sessions || "0",
                lastActivity: userData.lastActive
                  ? new Date(
                    userData.lastActive.seconds * 1000
                  ).toLocaleDateString()
                  : invitation.lastActivity,
              };
            }
          } catch (profileError) {
            console.error(
              "Error fetching user data for accepted invitation:",
              profileError
            );
          }
        }
        return invitation;
      });

      return Promise.all(invitationsPromises);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch invitations");
    }
  }
);

// Fetch all team data (organization, teams, members, invitations)
export const fetchAllTeamData = createAsyncThunk(
  "team/fetchAllTeamData",
  async (
    {
      orgId,
      userId,
      currentId,
      userRole,
    }: { orgId: string; userId: string; currentId: string; userRole: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      // Fetch organization
      const orgResult = await dispatch(fetchOrganization(orgId)).unwrap();

      // Fetch the specific team for the current user
      const teamResult = await dispatch(
        fetchTeams({ orgId, teamId: userId })
      ).unwrap();

      console.log("fetchAllTeamData - teamResult:", teamResult);

      let teams = teamResult;
      let currentUserTeam = teams.length > 0 ? teams[0] : null;
      let usersSnapshot: any = { docs: [], teamData: null };

      if (currentUserTeam && currentUserTeam.users?.length > 0) {
        // Get all user IDs from the current user's team
        const teamUserIds = currentUserTeam.users.map((u: any) => u.userId);

        // Fetch users from the users collection
        const fetchUsersResult = await dispatch(
          fetchUsers({ userIds: teamUserIds, orgId })
        ).unwrap();

        // Convert to expected format
        if (
          "docs" in fetchUsersResult &&
          Array.isArray(fetchUsersResult.docs)
        ) {
          usersSnapshot = {
            docs: fetchUsersResult.docs,
            teamData: currentUserTeam,
          };
        } else {
          usersSnapshot = {
            docs: (fetchUsersResult as any).docs.map((doc: any) => ({
              id: doc.id,
              data: () => doc.data(),
            })),
            teamData: currentUserTeam,
          };
        }
      }

      const ownerUserId = (orgResult as any)?.owner?.owner || null;

      // Build member data with profile information
      const membersFromUsers = await dispatch(
        buildMemberData({ snapshot: usersSnapshot, teams, ownerUserId })
      ).unwrap();

      // Fetch profiles for all members to enhance the data
      const memberUserIds = membersFromUsers.map(
        (member: any) => member.uid || member.id
      );
      const profilesData = await dispatch(
        fetchMembersProfiles(memberUserIds)
      ).unwrap();

      // Enhance members with profile data
      const enhancedMembers = membersFromUsers.map((member: any) => {
        const profileData = profilesData[member.uid || member.id];
        if (profileData) {
          return {
            ...member,
            // Override with profile data if available
            name: profileData.name || member.name,
            email: profileData.email || member.email,
            avatar: profileData.profileImageUrl || member.avatar,
            rate: profileData.ratePerMinute
              ? `$${Number(profileData.ratePerMinute).toFixed(2)}`
              : member.rate,
            specialties:
              profileData.specialties ||
              profileData.expertise ||
              member.specialties ||
              [],
            createdAt: profileData.createdAt || member.createdAt,
            profileData: profileData,
          };
        }
        member.sort((a: any, b: any) => {
          const timeA = a.startTime?.seconds || a.createdAt?.seconds || 0;
          const timeB = b.startTime?.seconds || b.createdAt?.seconds || 0;
          return timeB - timeA;
        });
        return member;
      });

      // Fetch invitations for the current user's team
      const invitedMembers = await dispatch(
        fetchInvitations({ orgId, teamId: userId })
      ).unwrap();

      // Merge members
      const uniqueMembers = mergeMembers(
        enhancedMembers,
        invitedMembers,
        userRole,
        teams,
        userId
      );

      return {
        organization: orgResult,
        teams,
        members: uniqueMembers.filter((d) => d.uid !== currentId),
        currentTeam: currentUserTeam,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch team data");
    }
  }
);

// Utility function to merge members and invites with team-based filtering
const mergeMembers = (
  members: any[],
  invites: any[],
  role: string,
  teams: Team[],
  currentUserId?: string
): any[] => {
  const emailsSet = new Set<string>();
  const userIdsSet = new Set<string>();
  const result: any[] = [];

  const addIfUnique = (member: any) => {
    const email = member.email.toLowerCase();
    const userId = member.uid || member.id;

    // Skip if we already have this user by email or ID
    if (emailsSet.has(email) || userIdsSet.has(userId)) {
      return;
    }

    emailsSet.add(email);
    userIdsSet.add(userId);
    result.push(member);
  };

  // Get team user IDs for filtering - only from current user's team
  const teamUserIds = new Set<string>();
  const currentUserTeam = teams.find((team) => team.id === currentUserId);
  if (currentUserTeam) {
    currentUserTeam.users?.forEach((user) => {
      if (user.userId) {
        teamUserIds.add(user.userId);
      }
    });
  }

  console.log("mergeMembers DEBUG:", {
    role,
    membersCount: members.length,
    invitesCount: invites.length,
    teamUserIds: Array.from(teamUserIds),
    currentUserId,
    currentUserTeam: currentUserTeam?.teamName,
  });

  // Filter invitations based on teamId matching invitedById
  const teamSpecificInvites = invites.filter((invite: any) => {
    // Check if invitation was sent by the current user/team
    return invite.invitedById === currentUserId;
  });

  console.log("teamSpecificInvites:", teamSpecificInvites.length);

  // Show all users in the team - remove role-based filtering
  // Add all active members who are in the current user's team
  const activeMembersToAdd = members.filter(
    (m) => teamUserIds.has(m.uid || m.id) || m.isOwner
  );

  // Add all team-specific invites
  const invitesToAdd = teamSpecificInvites;

  // Add active members first (they take priority)
  activeMembersToAdd.forEach(addIfUnique);

  // Then add invites only if they don't conflict with active users
  invitesToAdd.forEach((invite) => {
    // For accepted invitations, merge with existing user data if found
    if (invite.status === "active") {
      const existingMember = result.find(
        (m) =>
          m.email.toLowerCase() === invite.email.toLowerCase() ||
          (m.uid || m.id) === (invite.uid || invite.id)
      );

      if (existingMember) {
        // Merge invitation data into existing member
        existingMember.invitedById = invite.invitedById;
        existingMember.invitedByName = invite.invitedByName;
        existingMember.invitationId = invite.invitationId;
      } else {
        addIfUnique(invite);
      }
    } else {
      // For pending invites, only add if no active user exists and invitedById matches current user
      const shouldIncludeInvite = invite.invitedById === currentUserId;
      if (shouldIncludeInvite) {
        addIfUnique(invite);
      }
    }
  });

  return result;
};

// Fetch and enhance filtered members with full profile data
export const fetchFilteredMembersProfiles = createAsyncThunk(
  "team/fetchFilteredMembersProfiles",
  async (members: Member[], { rejectWithValue }) => {
    try {
      if (!members || members.length === 0) return members;

      const enhancedMembers = await Promise.all(
        members.map(async (member) => {
          try {
            // Skip if member already has complete profile data
            if (member.profileData && member.rate && member.rate !== "$0.00") {
              return member;
            }

            // Fetch profile data from profiles collection
            const profilesRef = collection(db, "profiles");
            const profileQuery = query(
              profilesRef,
              where("userId", "==", member.uid || member.id)
            );
            const profileSnapshot = await getDocs(profileQuery);

            let profileData = null;
            let rate = member.rate || "$0.00";

            let specialties = member.specialties || [];

            if (!profileSnapshot.empty) {
              profileData = profileSnapshot.docs[0].data();

              // Extract rate per minute
              if (profileData.ratePerMinute || profileData.rate) {
                const rateValue = profileData.ratePerMinute || profileData.rate;
                rate = `$${Number(rateValue).toFixed(2)}`;
              }

              // Extract specialties
              if (
                profileData.specialties &&
                Array.isArray(profileData.specialties)
              ) {
                specialties = profileData.specialties;
              } else if (
                profileData.expertise &&
                Array.isArray(profileData.expertise)
              ) {
                specialties = profileData.expertise;
              }
            }

            // Calculate earnings from wallet transactions if not already available

            return {
              ...member,
              profileData: profileData,
              rate: rate,

              specialties: specialties,
              name: profileData?.name || member.name,
              email: profileData?.email || member.email,
              avatar: profileData?.profileImageUrl || member.avatar,
              bio: profileData?.bio || member.bio || "",
              experience: profileData?.experience || member.experience || "",
              languages: profileData?.languages || member.languages || [],
              timeZone: profileData?.timeZone || member.timeZone || "",
              createdAt: profileData?.createdAt,
              availability:
                profileData?.availability !== undefined
                  ? profileData.availability
                  : member.availability,
            };
          } catch (error) {
            console.error(
              `Error fetching profile for member ${member.uid || member.id}:`,
              error
            );
            return member; // Return original member if profile fetch fails
          }
        })
      );

      return enhancedMembers;
    } catch (error: any) {
      console.error("Error fetching filtered members profiles:", error);
      return rejectWithValue(
        error?.message || "Failed to fetch filtered members profiles"
      );
    }
  }
);

// Enhance members with profile data
export const enhanceMembersWithProfiles = createAsyncThunk(
  "team/enhanceMembersWithProfiles",
  async (members: Member[], { dispatch, rejectWithValue }) => {
    try {
      if (!members || members.length === 0) return members;

      // Get all user IDs that need profile data
      const userIds = members
        .map((member) => member.uid || member.id)
        .filter(Boolean);

      if (userIds.length === 0) return members;

      // Fetch profiles for all user IDs
      const profilesData = await dispatch(
        fetchMembersProfiles(userIds)
      ).unwrap();

      // Enhance each member with profile data
      const enhancedMembers = members.map((member) => {
        const userId = member.uid || member.id;
        const profileData = profilesData[userId];

        if (profileData) {
          return {
            ...member,
            // Enhance with profile data while preserving existing data
            name: profileData.name || member.name,
            email: profileData.email || member.email,
            avatar: profileData.profileImageUrl || member.avatar,
            rate: profileData.ratePerMinute
              ? `$${Number(profileData.ratePerMinute).toFixed(2)}`
              : member.rate || "$0.00",
            specialties:
              profileData.specialties ||
              profileData.expertise ||
              member.specialties ||
              [],
            profileData: profileData,
            // Additional profile fields
            bio: profileData.bio || "",
            experience: profileData.experience || "",
            languages: profileData.languages || [],
            timeZone: profileData.timeZone || "",
            availability:
              profileData.availability || member.availability || true,
          };
        }

        return member;
      });

      return enhancedMembers;
    } catch (error: any) {
      console.error("Error enhancing members with profiles:", error);
      return rejectWithValue(
        error?.message || "Failed to enhance members with profiles"
      );
    }
  }
);

// Fetch user's role in a specific team
export const fetchUserTeamRole = createAsyncThunk(
  "team/fetchUserTeamRole",
  async (
    {
      orgId,
      teamId,
      userId,
    }: { orgId: string; teamId: string; userId: string },
    { rejectWithValue }
  ) => {
    try {
      const teamDocRef = doc(db, `organizations/${orgId}/teams`, teamId);
      const teamDoc = await getDoc(teamDocRef);

      if (!teamDoc.exists()) {
        return rejectWithValue("Team not found");
      }
      const teamData = teamDoc.data();
      const userTeamRecord = teamData.users?.find(
        (user: any) => user.userId === userId
      );

      if (!userTeamRecord) {
        return rejectWithValue("User not found in team");
      }
      return userTeamRecord.role;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to fetch user team role"
      );
    }
  }
);

// Distribute funds to team members
export const distributeFunds = createAsyncThunk(
  "team/distributeFunds",
  async (
    {
      recipientIds,
      amount,
      distributionMethod,
      senderWalletId,
      senderId,
    }: {
      recipientIds: string[];
      amount: number;
      distributionMethod: "split" | "distribute";
      senderWalletId: string;
      senderId: string;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const results = [];
      let totalDistributed = 0;

      for (const recipientId of recipientIds) {
        const recipientAmount =
          distributionMethod === "split"
            ? amount / recipientIds.length
            : amount;

        // Here you would call your actual fund transfer service
        // For now, we'll simulate a successful transfer
        const transferResult = {
          success: true,
          recipientId,
          amount: recipientAmount,
        };

        if (transferResult.success) {
          totalDistributed += recipientAmount;
          results.push({
            recipientId,
            amount: recipientAmount,
            success: true,
          });
        } else {
          results.push({
            recipientId,
            amount: recipientAmount,
            success: false,
            error: "Transfer failed",
          });
        }
      }

      return {
        results,
        totalDistributed,
        distributionMethod,
      };
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to distribute funds");
    }
  }
);

// Update member wallet balance
export const updateMemberWalletBalance = createAsyncThunk(
  "team/updateMemberWalletBalance",
  async (
    { memberId, newBalance }: { memberId: string; newBalance: number },
    { rejectWithValue }
  ) => {
    try {
      // In a real implementation, you would update the wallet in your backend
      // For now, we'll return the updated balance
      return { memberId, newBalance };
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to update wallet balance"
      );
    }
  }
);

// Fetch team activities based on user role
export const fetchTeamActivities = createAsyncThunk(
  "team/fetchTeamActivities",
  async (
    {
      userRole,
      orgId,
      userId,
      currentTeam,
    }: {
      userRole: string;
      orgId: string;
      userId: string;
      currentTeam: Team | null;
    },
    { rejectWithValue }
  ) => {
    try {
      let activitiesQuery;
      const sessionActivityRef = collection(db, "sessionActivity");

      // Build query based on user role
      if (
        userRole === "customer_owner" ||
        userRole === "customer_team_lead" ||
        userRole === "customer_team_member"
      ) {
        // Expert owner sees all activities where expertOrganizationId matches their orgId
        activitiesQuery = query(
          sessionActivityRef,
          where("customerOrganizationId", "==", orgId)
        );
      } else if (
        userRole === "expert_owner" ||
        userRole === "expert_team_lead" ||
        userRole === "expert_team_member"
      ) {
        // Owner sees all activities where customerOrganizationId matches their orgId
        activitiesQuery = query(
          sessionActivityRef,
          where("expertOrganizationId", "==", orgId)
        );
      } else {
        // Default case - no activities
        return [];
      }

      const snapshot = await getDocs(activitiesQuery);
      return await processActivityDocuments(snapshot.docs);
    } catch (error: any) {
      console.error("Error fetching team activities:", error);
      return rejectWithValue(
        error?.message || "Failed to fetch team activities"
      );
    }
  }
);

// Helper function to process activity documents and fetch profile data
const processActivityDocuments = async (
  docs: any[]
): Promise<TeamActivity[]> => {
  try {
    if (docs.length === 0) return []; // Extract unique user IDs for batch profile fetching
    const userIds = new Set<string>();
    const activities = docs.map((doc) => {
      const data = doc.data();
      if (data.customerId) userIds.add(data.customerId);
      if (data.expertId) userIds.add(data.expertId);

      return {
        id: doc.id,
        ...data,
        // Ensure duration is properly formatted
        duration: data.duration || 0,
      } as TeamActivity;
    });

    // Batch fetch profiles for all users
    const profilesData = await batchFetchProfiles(Array.from(userIds));

    // Enhance activities with profile data
    const enhancedActivities = activities.map((activity) => {
      // Add customer profile data
      if (activity.customerId && profilesData[activity.customerId]) {
        const customerProfile = profilesData[activity.customerId];
        activity.customerData = {
          name:
            customerProfile.name || customerProfile.email || "Unknown Customer",
          email: customerProfile.email || "",
          teamRole: customerProfile.teamRole || "",
          profileImageUrl: customerProfile.profileImageUrl,
          initials: getInitials(
            customerProfile.name || customerProfile.email || "Unknown"
          ),
        };
      }

      // Add expert profile data
      if (activity.expertId && profilesData[activity.expertId]) {
        const expertProfile = profilesData[activity.expertId];
        activity.expertData = {
          name: expertProfile.name || "Unknown Expert",
          email: expertProfile.email || "",
          teamRole: expertProfile.teamRole || "",
          profileImageUrl: expertProfile.profileImageUrl,
          initials: getInitials(
            expertProfile.name || expertProfile.email || "Unknown"
          ),
        };
      }

      return activity;
    });

    // Sort by start time (newest first)
    enhancedActivities.sort((a, b) => {
      const timeA = a.startTime?.seconds || a.createdAt?.seconds || 0;
      const timeB = b.startTime?.seconds || b.createdAt?.seconds || 0;
      return timeB - timeA;
    });

    return enhancedActivities;
  } catch (error) {
    console.error("Error processing activity documents:", error);
    return [];
  }
};

// Helper function to batch fetch profiles
const batchFetchProfiles = async (
  userIds: string[]
): Promise<Record<string, any>> => {
  try {
    if (userIds.length === 0) return {};

    const profilesData: Record<string, any> = {};

    // First, try to fetch from profiles collection
    const profilesRef = collection(db, "profiles");

    // Batch queries for profiles (max 10 per query due to Firestore limitation)
    const profileBatches = [];
    for (let i = 0; i < userIds.length; i += 10) {
      const batch = userIds.slice(i, i + 10);
      const batchQuery = query(profilesRef, where("userId", "in", batch));
      profileBatches.push(getDocs(batchQuery));
    }

    const profileResults = await Promise.all(profileBatches);

    // Process profile results
    profileResults.forEach((snapshot) => {
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (data.userId) {
          profilesData[data.userId] = data;
        }
      });
    }); // For users not found in profiles, fetch from users collection
    const missingUserIds = userIds.filter((id) => !profilesData[id]);

    if (missingUserIds.length > 0) {
      const userBatches = [];
      for (let i = 0; i < missingUserIds.length; i += 10) {
        const batch = missingUserIds.slice(i, i + 10);
        const promises = batch.map((userId) =>
          getDoc(doc(db, "users", userId))
        );
        userBatches.push(Promise.all(promises));
      }

      const userResults = await Promise.all(userBatches);

      userResults.flat().forEach((userDoc) => {
        if (userDoc.exists()) {
          const userData = userDoc.data();
          profilesData[userDoc.id] = {
            userId: userDoc.id,
            name: userData.name,
            email: userData.email,
            profileImageUrl: userData.profileImageUrl,
            ...userData,
          };
        }
      });
    }

    return profilesData;
  } catch (error) {
    console.error("Error batch fetching profiles:", error);
    return {};
  }
};

// Helper function to generate initials
const getInitials = (name: string): string => {
  if (!name) return "??";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
};

// Real-time listener specifically for current team
export const setupCurrentTeamListener = createAsyncThunk(
  "team/setupCurrentTeamListener",
  async (
    {
      orgId,
      userId,
    }: { orgId: string; userId: string },
    { dispatch }
  ) => {
    try {
      // Clean up previous current team listener
      if (currentTeamUnsubscribe) {
        currentTeamUnsubscribe();
        currentTeamUnsubscribe = null;
      }

      // Listen to current team document (specific team for the user)
      const currentTeamRef = doc(db, `organizations/${orgId}/teams`, userId);
      currentTeamUnsubscribe = onSnapshot(
        currentTeamRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const teamData = { id: snapshot.id, ...snapshot.data() } as Team;
            console.log("Current team updated in real-time:", teamData);
            dispatch(setCurrentTeam(teamData));
          } else {
            console.log("Current team document does not exist");
            dispatch(setCurrentTeam(null));
          }
        },
        (error) => {
          console.error("Error listening to current team:", error);
          return { success: false, error: error.message };
        }
      );

      return { success: true, listenerId: "currentTeam" };
    } catch (error: any) {
      console.error("Error setting up current team listener:", error);
      return { success: false, error: error.message };
    }
  }
);

// Clean up only the current team listener
export const cleanupCurrentTeamListener = createAsyncThunk(
  "team/cleanupCurrentTeamListener",
  async () => {
    try {
      if (currentTeamUnsubscribe) {
        currentTeamUnsubscribe();
        currentTeamUnsubscribe = null;
      }
      return { success: true };
    } catch (error: any) {
      console.error("Error cleaning up current team listener:", error);
      return { success: false, error: error.message };
    }
  }
);

// Add global variables to store unsubscribe functions for each listener
let usersUnsubscribe: (() => void) | null = null;
let profilesUnsubscribe: (() => void) | null = null;
let walletsUnsubscribe: (() => void) | null = null;
let sessionActivityUnsubscribe: (() => void) | null = null;
let currentTeamUnsubscribe: (() => void) | null = null;
let teamsUnsubscribe: (() => void) | null = null;

export const setupTeamRealtimeListeners = createAsyncThunk(
  "team/setupTeamRealtimeListeners",
  async (
    {
      orgId,
      userId,
      currentId,
      userRole,
    }: { orgId: string; userId: string; currentId: string; userRole: string },
    { dispatch }
  ) => {
    // Clean up previous listeners
    if (usersUnsubscribe) usersUnsubscribe();
    if (profilesUnsubscribe) profilesUnsubscribe();
    if (walletsUnsubscribe) walletsUnsubscribe();
    if (sessionActivityUnsubscribe) sessionActivityUnsubscribe();
    if (currentTeamUnsubscribe) currentTeamUnsubscribe();
    if (teamsUnsubscribe) teamsUnsubscribe();

    // Listen to current team document (specific team for the user)
    const currentTeamRef = doc(db, `organizations/${orgId}/teams`, userId);
    currentTeamUnsubscribe = onSnapshot(currentTeamRef, (snapshot) => {
      if (snapshot.exists()) {
        const teamData = { id: snapshot.id, ...snapshot.data() } as Team;
        console.log("Current team updated in real-time:", teamData);
        dispatch(setCurrentTeam(teamData));
      } else {
        console.log("Current team document does not exist");
        dispatch(setCurrentTeam(null));
      }
    }, (error) => {
      console.error("Error listening to current team:", error);
    });

    // Listen to all teams in the organization
    const teamsRef = collection(db, `organizations/${orgId}/teams`);
    teamsUnsubscribe = onSnapshot(teamsRef, (snapshot) => {
      const teams = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Team));
      console.log("Teams collection updated in real-time:", teams.length);
      dispatch(updateTeamsInState(teams));
    }, (error) => {
      console.error("Error listening to teams collection:", error);
    });

    // Listen to users collection
    const usersRef = query(
      collection(db, "users"),
      where("orgId", "==", orgId)
    );
    usersUnsubscribe = onSnapshot(usersRef, () => {
      dispatch(fetchAllTeamData({ orgId, userId, currentId, userRole }));
    });

    // Listen to profiles collection
    const profilesRef = query(
      collection(db, "profiles"),
      where("orgId", "==", orgId)
    );
    profilesUnsubscribe = onSnapshot(profilesRef, () => {
      dispatch(fetchAllTeamData({ orgId, userId, currentId, userRole }));
    });

    // Listen to wallets collection
    const walletsRef = collection(db, "wallets");
    walletsUnsubscribe = onSnapshot(walletsRef, () => {
      dispatch(fetchAllTeamData({ orgId, userId, currentId, userRole }));
    });

    // Listen to sessionActivity collection
    const sessionActivityRef = collection(db, "sessionActivity");
    sessionActivityUnsubscribe = onSnapshot(sessionActivityRef, () => {
      dispatch(fetchAllTeamData({ orgId, userId, currentId, userRole }));
    });

    // Store listeners for cleanup
    dispatch(
      setRealtimeListeners([
        usersUnsubscribe,
        profilesUnsubscribe,
        walletsUnsubscribe,
        sessionActivityUnsubscribe,
        currentTeamUnsubscribe,
        teamsUnsubscribe,
      ])
    );
    return { success: true };
  }
);

export const cleanupTeamRealtimeListeners = createAsyncThunk(
  "team/cleanupTeamRealtimeListeners",
  async (_, { dispatch }) => {
    if (usersUnsubscribe) usersUnsubscribe();
    if (profilesUnsubscribe) profilesUnsubscribe();
    if (walletsUnsubscribe) walletsUnsubscribe();
    if (sessionActivityUnsubscribe) sessionActivityUnsubscribe();
    if (currentTeamUnsubscribe) currentTeamUnsubscribe();
    if (teamsUnsubscribe) teamsUnsubscribe();
    
    usersUnsubscribe = null;
    profilesUnsubscribe = null;
    walletsUnsubscribe = null;
    sessionActivityUnsubscribe = null;
    currentTeamUnsubscribe = null;
    teamsUnsubscribe = null;
    
    dispatch(setRealtimeListeners([]));
    return { success: true };
  }
);

const teamSlice = createSlice({
  name: "team",
  initialState,
  reducers: {
    setCurrentTeam: (state, action) => {
      state.currentTeam = action.payload;
    },
    filterMembersTeams: (state, action) => {
      state.filteredMembersLoading = true; // <-- set loading true at start
      const { searchTerm, userRole, userId } = action.payload;

      // Store the applied filters for later use
      state.lastAppliedFilters = { searchTerm, userRole, userId };

      let filtered = [...state.members];
      console.log(" state.teams", state);
      // Get team user IDs for filtering - only from current user's team
      const teamUserIds = new Set<string>();
      const currentUserTeam = state.teams.find((team) => team.id === userId);
      if (currentUserTeam) {
        currentUserTeam.users?.forEach((user: any) => {
          if (user.userId) {
            teamUserIds.add(user.userId);
          }
        });
      }

      // Show all users in the team - no role-based filtering
      // Include all members who are in the current user's team, are owners, or were invited by current user
      filtered = state.members.filter(
        (member) =>
          teamUserIds.has(member.uid || member.id) ||
          member.isOwner ||
          (member.status === "invited" && member.invitedById === userId)
      );

      // If userRole is team lead, only show team members
      if (
        userRole === "customer_team_lead" ||
        userRole === "expert_team_lead"
      ) {
        filtered = filtered.filter(
          (member) =>
            member.role === "expert_team_member" ||
            member.role === "customer_team_member" ||
            member.profileData?.teamRole === "expert_team_member" ||
            member.profileData?.teamRole === "customer_team_member"
        );
      }

      // Apply search filter
      if (searchTerm && searchTerm.trim()) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(
          (member) => {
            const name = member.name?.toLowerCase() || '';
            const email = member.email?.toLowerCase() || '';
            const role = member.role?.toLowerCase() || '';
            const profileRole = member.profileData?.teamRole?.toLowerCase() || '';
            
            return (
              name.includes(search) ||
              email.includes(search) ||
              role.includes(search) ||
              profileRole.includes(search)
            );
          }
        );
      }

      console.log('Filter result:', { 
        searchTerm, 
        originalCount: state.members.length,
        teamFilteredCount: filtered.length,
        teamId: userId 
      });

      state.filteredMembers = filtered;
      state.filteredMembersLoading = false; // <-- set loading false after filtering
    },
    setEnhancedMembers: (state, action) => {
      state.members = action.payload;
      state.filteredMembers = action.payload;
      state.lastUpdated = new Date().toISOString();
      state.filteredMembersLoading = false; // <-- ensure loading false after enhancement
    },
    updateMemberBalanceInState: (state, action) => {
      const { memberId, newBalance } = action.payload;

      // Update in members array
      const memberIndex = state.members.findIndex(
        (m) => m.id === memberId || m.uid === memberId
      );
      if (memberIndex !== -1) {
        state.members[memberIndex].wallet = `$${newBalance.toFixed(2)}`;
      }

      // Update in filtered members array
      const filteredMemberIndex = state.filteredMembers.findIndex(
        (m) => m.id === memberId || m.uid === memberId
      );
      if (filteredMemberIndex !== -1) {
        state.filteredMembers[
          filteredMemberIndex
        ].wallet = `$${newBalance.toFixed(2)}`;
      }

      // Update last updated timestamp
      state.lastUpdated = new Date().toISOString();
    },
    addMemberToState: (state, action) => {
      // Add the new member to both members and filteredMembers arrays
      const newMember = action.payload;
      // Prevent duplicates by checking uid or id
      const exists = state.members.some(
        (m) => m.id === newMember.id || m.uid === newMember.uid
      );
      if (!exists) {
        state.members.unshift(newMember);
        state.filteredMembers.unshift(newMember);
      }
      // Update last updated timestamp
      state.lastUpdated = new Date().toISOString();
    },
    // New action to set real-time listeners
    setRealtimeListeners: (state, action) => {
      state.realtimeListeners = action.payload;
    },
    // New action to update teams in state from real-time updates
    updateTeamsInState: (state, action) => {
      const updatedTeams = action.payload;
      state.teams = updatedTeams;
      
      // Update currentTeam if it exists in the updated teams
      if (state.currentTeam) {
        const updatedCurrentTeam = updatedTeams.find(
          (team: Team) => team.id === state.currentTeam?.id
        );
        if (updatedCurrentTeam) {
          state.currentTeam = updatedCurrentTeam;
        }
      }
      
      state.lastUpdated = new Date().toISOString();
    },
    // New action to update single member in real-time
    updateMemberInState: (state, action) => {
      const updatedMember = action.payload;
      const memberId = updatedMember.id || updatedMember.uid;

      // Update in members array
      const memberIndex = state.members.findIndex(
        (m) => m.id === memberId || m.uid === memberId
      );
      if (memberIndex !== -1) {
        state.members[memberIndex] = {
          ...state.members[memberIndex],
          ...updatedMember,
        };
      }

      // Update in filtered members array
      const filteredIndex = state.filteredMembers.findIndex(
        (m) => m.id === memberId || m.uid === memberId
      );
      if (filteredIndex !== -1) {
        state.filteredMembers[filteredIndex] = {
          ...state.filteredMembers[filteredIndex],
          ...updatedMember,
        };
      }

      // Update last updated timestamp
      state.lastUpdated = new Date().toISOString();
      state.filteredMembersLoading = false; // <-- ensure loading false after update
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchAllTeamData cases
      .addCase(fetchAllTeamData.pending, (state) => {
        state.status = "loading";
        state.filteredMembersLoading = true; // <-- set loading true on fetch start
      })
      .addCase(fetchAllTeamData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.organization = action.payload.organization;
        state.teams = action.payload.teams;
        state.members = action.payload.members;
        state.filteredMembers = action.payload.members;

        // Set current team from payload or use default logic
        if (action.payload.currentTeam) {
          state.currentTeam = action.payload.currentTeam;
        } else if (!state.currentTeam && action.payload.teams.length > 0) {
          const defaultTeam = action.payload.teams.find((team) => team.default);
          state.currentTeam = defaultTeam || action.payload.teams[0];
        }
        state.filteredMembersLoading = false; // <-- set loading false on fetch complete
      })
      .addCase(fetchAllTeamData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
        state.filteredMembersLoading = false; // <-- set loading false on fetch error
      })

      // fetchOrganization cases
      .addCase(fetchOrganization.fulfilled, (state, action) => {
        state.organization = action.payload;
      })

      // fetchTeams cases
      .addCase(fetchTeams.fulfilled, (state, action) => {
        state.teams = action.payload;
        if (!state.currentTeam && action.payload.length > 0) {
          const defaultTeam = action.payload.find((team) => team.default);
          state.currentTeam = defaultTeam || action.payload[0];
        }
      })
      .addCase(fetchUserTeamRole.fulfilled, (state, action) => {
        state.teamRole = action.payload;
      })

      // fetchTeamActivities cases
      .addCase(fetchTeamActivities.pending, (state) => {
        state.activitiesLoading = true;
        state.activitiesError = null;
      })
      .addCase(fetchTeamActivities.fulfilled, (state, action) => {
        state.activitiesLoading = false;
        state.teamActivities = action.payload;
        state.activitiesError = null;
      })
      .addCase(fetchTeamActivities.rejected, (state, action) => {
        state.activitiesLoading = false;
        state.activitiesError = action.payload as string;
      })

      // distributeFunds cases
      .addCase(distributeFunds.pending, (state) => {
        state.status = "loading";
      })
      .addCase(distributeFunds.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.totalDistributed += action.payload.totalDistributed;
      })
      .addCase(distributeFunds.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      }) // updateMemberWalletBalance cases
      .addCase(updateMemberWalletBalance.fulfilled, (state, action) => {
        const { memberId, newBalance } = action.payload;
        const memberIndex = state.members.findIndex(
          (m) => m.id === memberId || m.uid === memberId
        );
        if (memberIndex !== -1) {
          state.members[memberIndex].wallet = `$${newBalance.toFixed(2)}`;
        }

        const filteredMemberIndex = state.filteredMembers.findIndex(
          (m) => m.id === memberId || m.uid === memberId
        );
        if (filteredMemberIndex !== -1) {
          state.filteredMembers[
            filteredMemberIndex
          ].wallet = `$${newBalance.toFixed(2)}`;
        }

        // Update last updated timestamp
        state.lastUpdated = new Date().toISOString();
      })

      // enhanceMembersWithProfiles cases
      .addCase(enhanceMembersWithProfiles.pending, (state) => {
        state.status = "loading";
      })
      .addCase(enhanceMembersWithProfiles.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.members = action.payload;
        state.filteredMembers = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(enhanceMembersWithProfiles.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      }) // fetchMembersProfiles cases
      .addCase(fetchMembersProfiles.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchMembersProfiles.fulfilled, (state) => {
        state.status = "succeeded";
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchMembersProfiles.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })

      // fetchFilteredMembersProfiles cases
      .addCase(fetchFilteredMembersProfiles.pending, (state) => {
        state.status = "loading";
        state.filteredMembersLoading = true; // <-- set loading true on filtered fetch start
      })
      .addCase(fetchFilteredMembersProfiles.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.filteredMembers = action.payload;
        state.lastUpdated = new Date().toISOString();
        state.filteredMembersLoading = false; // <-- set loading false on filtered fetch complete
      })
      .addCase(fetchFilteredMembersProfiles.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
        state.filteredMembersLoading = false; // <-- set loading false on filtered fetch error
      })

      // updateMemberAfterSave cases
      .addCase(updateMemberAfterSave.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateMemberAfterSave.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updateMemberAfterSave.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })

      // setupRealtimeListeners cases
      .addCase(setupRealtimeListeners.pending, (state) => {
        state.status = "loading";
      })
      .addCase(setupRealtimeListeners.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(setupRealtimeListeners.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })

      // updateMembersFromRealtimeData cases
      .addCase(updateMembersFromRealtimeData.fulfilled, (state, action) => {
        if (action.payload.members && action.payload.members.length > 0) {
          state.members = action.payload.members;
          state.filteredMembers =
            action.payload.filtered || action.payload.members;
        }
        state.lastUpdated = new Date().toISOString();
      })

      // cleanupRealtimeListeners cases
      .addCase(cleanupRealtimeListeners.fulfilled, (state) => {
        state.realtimeListeners = [];
        state.lastUpdated = new Date().toISOString();
      })

      // setupCurrentTeamListener cases
      .addCase(setupCurrentTeamListener.pending, (state) => {
        state.status = "loading";
      })
      .addCase(setupCurrentTeamListener.fulfilled, (state) => {
        state.status = "succeeded";
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(setupCurrentTeamListener.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to setup current team listener";
      })

      // cleanupCurrentTeamListener cases
      .addCase(cleanupCurrentTeamListener.fulfilled, (state) => {
        state.lastUpdated = new Date().toISOString();
      });
  },
});

export const {
  setCurrentTeam,
  filterMembersTeams,
  setEnhancedMembers,
  updateMemberBalanceInState,
  addMemberToState,
  setRealtimeListeners,
  updateTeamsInState,
  updateMemberInState,
} = teamSlice.actions;

export default teamSlice.reducer;
